import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import BreadCrumbs from "../../components/common/BreadCrumbs";
import FormButton from "../../components/Button";
import Loader from "../../components/loader/Loading";
import { getReq, postReq } from "../../apis/api";
import { useSnackbar } from 'notistack';
import NotificationTable from "../../components/DataTable/NotificationTable";
import Add from "../../components/Modal/notification/Add";
import ConfirmationModal from "../../components/Modal/confirmationmodal/ConfirmationModal";
import { dateTime } from "../../utils/format";
import { currentRoleId } from "../../utils/session";

const Notification = () => {
  const [openModal, setOpenModal] = useState(false);
  const [customers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [confirmationModal, setConfirmationModal] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  // P061: Send now is for whoever may create notifications (Notifications create flag; the server checks the same)
  const { permissions } = useSelector((state) => state.access);
  const { roleName } = useSelector((state) => state.role);
  const canSend =
    Number(currentRoleId()) === 1 ||
    Boolean(permissions?.[roleName]?.modules?.["Notifications"]?.pages?.["Notifications"]?.sections?.["Notifications"]?.create);

  const handleButtonClick = () => {
    setOpenModal(!openModal);
  };

  useEffect(() => {
    fetchPaymentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loads the notifications once on mount
  }, []);

  const fetchPaymentData = async () => {
    try {
      setIsLoading(true);
      const response = await getReq("/fetchNotifications");

      if (response.status === 200) {
        const sortedDataDescending = response.data.sort((a, b) => b.id - a.id);
        setPayments(sortedDataDescending);
      }
    } catch (error) {
      console.error("Error", error);
      enqueueSnackbar(error?.response?.data?.message || "Error in Fetching Notifications", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // P061: send a template at once (notifications/send marks it Sent, so its schedule does not send it again),
  // after a confirmation that says how many people it reaches (recipient_count: the active accounts it goes to)
  const handleSendNow = (row) => {
    const count = Number(row.recipient_count || 0);
    if (count === 0) {
      enqueueSnackbar("This notification reaches no active user.", { variant: "warning" });
      return;
    }
    setConfirmationModal({
      text1: `Send "${row.title}" now?`,
      text2: `It goes to ${count} ${count === 1 ? "person" : "people"} now and is not sent again at its schedule.`,
      btn1Text: "Send now",
      btn2Text: "Cancel",
      btn1Handler: async () => {
        setConfirmationModal(null);
        try {
          const response = await postReq({ template_id: row.id }, "notifications/send");
          enqueueSnackbar(response?.data?.message || "Notification sent", { variant: "success" });
          fetchPaymentData();
        } catch (error) {
          enqueueSnackbar(error?.response?.data?.message || "Notification not sent", { variant: "error" });
        }
      },
      btn2Handler: () => setConfirmationModal(null),
    });
  };

  const columns = [
    {
      name: "#",
      selector: (row, index) => index + 1,
      sortable: false,
      width: "64px",
      center: true,
    },
    {
      name: "Title",
      selector: (row) => row.title || "N/A",
      sortable: true,
    },
    {
      name: "Description",
      selector: (row) => row.description || "N/A",
      sortable: true,
      wrap: true,
    },
    {
      // P060: the template's user types by name ("Dealer, Customer")
      name: "User Type",
      selector: (row) => row.role_name || "N/A",
      sortable: true,
    },
    {
      name: "User Name",
      selector: (row) => row.user_name || "N/A",
      sortable: true,
      wrap: true,
    },
    {
      name: "Schedule",
      selector: (row) => row.schedule || "N/A",
      format: (row) => dateTime(row.schedule), // FIX_RULES 10.3: one date format
      sortable: true,
      wrap: true,
    },
    {
      name: "Status",
      selector: (row) => row.status || "N/A",
      sortable: true,
      wrap: true,
    },
  ];
  return (
    <div>
      {confirmationModal && <ConfirmationModal modalData={confirmationModal} />}
      <div className="flex justify-between items-center bg-white rounded-[0.42vw] w-full py-[1vw] md:py-[0.5vw] my-[2vw] md:my-[1vw] px-[4vw] md:px-[2vw] ">
        <BreadCrumbs title={"Home / Notification"} />
        <FormButton title="Send Notification" func={handleButtonClick} />
      </div>

      <main>
        {isLoading ? (
          <Loader />
        ) : (
          <NotificationTable
            columns={columns}
            data={payments}
            onItemAdded={fetchPaymentData}
            customers={customers}
            onSendNow={canSend ? handleSendNow : null}
          />
        )}
      </main>

      {openModal && (
        <Add
          open={openModal}
          handleClose={handleButtonClick}
          onItemAdded={fetchPaymentData}
        />
      )}
    </div>
  );
};

export default Notification;
